/**
 * Feature Flags API + Evaluator - Mandate C (dead-end protection).
 *
 * Backend exposes dynamic Redis-backed feature toggles at
 * /api/v1/features (FeatureFlagsResponse). The dynamic flags can flip at
 * runtime (operational toggles), so a hook in the web app fetches them via
 * TanStack Query and forwards them here to *evaluate* a given flag.
 *
 * Keeping the evaluator pure (no React, no network) makes it trivially
 * unit-testable in packages/core's vitest suite (no jsdom pairing needed).
 */

import { apiClient } from './apiClient';
import type { FeatureFlagsResponse } from '../../types/rest';
import type { FeatureFlag } from '../../types/common';

/**
 * Canonical feature flag shape, mirroring FeatureFlagsResponse exactly so
 * the evaluator cannot drift from the persisted server model.
 */
export type FeatureFlagSnapshot = FeatureFlagsResponse;

/**
 * Permissive sentinel peek-through: a flag absent from the snapshot is
 * treated as enabled. This matches the backend's "extra='forbid' but new
 * flags default on" posture: when a flag isn't sent by the server yet it
 * either doesn't exist or is implicitly on.
 */
export const FEATURE_FLAG_DEFAULTS: FeatureFlagSnapshot = {
  ai_enabled: true,
  pvp_enabled: true,
  replays_enabled: true,
  analytics_outbox_enabled: false,
  tickets_enabled: false,
  rooms_enabled: false,
  tournaments_enabled: false,
  spectating_enabled: false,
} as const;

/**
 * Feature flag aliases: the HomeHub references user-facing feature areas
 * (e.g. "friend-rooms", "tournaments", "store") that map to one or more
 * wire flags. Centralizing the alias map means screens ask for a feature,
 * not a transport key.
 */
export const FEATURE_FLAG_ALIASES: Record<string, FeatureFlag> = {
  'friend-rooms': 'rooms_enabled',
  tournaments: 'tournaments_enabled',
  store: 'tickets_enabled',
  'quick-match': 'pvp_enabled',
  leaderboard: 'replays_enabled',
  profile: 'replays_enabled',
  spectating: 'spectating_enabled',
  'ai-play': 'ai_enabled',
} as const;

/**
 * All known wire flags - used by the alias resolver to distinguish an
 * unknown alias (forbid) from a missing snapshot field (permissive).
 */
export const KNOWN_FEATURE_FLAGS: readonly FeatureFlag[] = [
  'ai_enabled',
  'pvp_enabled',
  'replays_enabled',
  'analytics_outbox_enabled',
  'tickets_enabled',
  'rooms_enabled',
  'tournaments_enabled',
  'spectating_enabled',
] as const;

/**
 * Fetch the live feature flag snapshot from the server.
 * GET /api/v1/features
 * 200 -> FeatureFlagsResponse
 * 503 -> backend L1 cache down (caller falls back to defaults)
 */
export async function getFeatureFlags(): Promise<FeatureFlagsResponse> {
  const response = await apiClient.get<FeatureFlagsResponse>('/features');
  return response.data;
}

/**
 * Resolve an alias (e.g. "friend-rooms") into the canonical wire flag
 * (e.g. "rooms_enabled"), or pass through a wire flag verbatim.
 * Returns null for an unrecognised alias (treated as disabled).
 */
export function resolveFeatureFlag(flag: string): FeatureFlag | null {
  if ((KNOWN_FEATURE_FLAGS as readonly string[]).includes(flag)) {
    return flag as FeatureFlag;
  }
  return FEATURE_FLAG_ALIASES[flag] ?? null;
}

/**
 * Evaluate a single feature flag against a snapshot using the permissive
 * default-merge strategy:
 *  - Unknown alias  -> false (the area is not wired).
 *  - Known wire flag missing from the snapshot -> FEATURE_FLAG_DEFAULTS.
 *  - Otherwise the snapshot value wins.
 *
 * Pure + deterministic so it can be exhaustively unit tested.
 */
export function evaluateFeatureFlag(
  snapshot: FeatureFlagSnapshot | null,
  flag: string,
): boolean {
  const wireFlag = resolveFeatureFlag(flag);
  if (wireFlag === null) {
    return false;
  }
  const fromSnapshot = snapshot?.[wireFlag];
  if (typeof fromSnapshot === 'boolean') {
    return fromSnapshot;
  }
  return FEATURE_FLAG_DEFAULTS[wireFlag];
}

/**
 * Merge a partial server snapshot over the defaults so consumers never have
 * to null-check each field.
 */
export function normalizeFeatureFlags(
  snapshot: Partial<FeatureFlagsResponse> | null,
): FeatureFlagSnapshot {
  return { ...FEATURE_FLAG_DEFAULTS, ...(snapshot ?? {}) };
}
